import { Button } from 'components/Button/Button';
import { Input } from 'components/Input/Input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from 'components/InputOTP/InputOPT';
import { Label } from 'components/Label/Label';
import { useToast } from 'components/Toast/use-toast';
import { Typography } from 'components/Typograhpy/Typography';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import Cookies from 'js-cookie';
import { EMAIL_REGEX, cn } from 'lib/utils';
import { Loader2, Smartphone, KeyRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import Countdown from 'react-countdown';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { sendOtp, userLogin, check2FAMethod, verifyTotpLogin } from 'services/auth';

import styles from './Login.module.scss';
let NOW = Date.now();

type TwoFAStep = 'credentials' | 'sms-otp' | 'totp';

export const Login = () => {
    const [formCredentials, setFormCredentials] = useState<any>(null);
    const [otpValue, setOtpValue] = useState<any>(null);
    const [step, setStep] = useState<TwoFAStep>('credentials');
    const [phoneNo, setPhoneNo] = useState<string | null>(null);
    const [otpMethod, setOtpMethod] = useState<'email' | 'sms' | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showCounter, setShowCounter] = useState(true);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t } = useTranslation('auth');
    const base = process.env.REACT_APP_BASENAME && process.env.REACT_APP_BASENAME.replace(/\/$/, '');
    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors, isValid },
    } = useForm({
        mode: 'onChange',
    });

    useEffect(() => {
        const token = Cookies.get('token');
        if (token) {
            navigate(AppRoutes.dashboard, { replace: true });
        }
    }, [navigate]);

    const resendCode = () => {
        NOW = Date.now();
        setShowCounter(true);
        sendSmsOtp(formCredentials);
    };

    // Step 1: Check 2FA method after credentials are entered
    const onSubmit = async (formData: any) => {
        setFormCredentials(formData);
        setIsLoading(true);
        
        try {
            // First, check what 2FA method the user has configured
            const result: any = await check2FAMethod(formData.email, formData.password);
            
            if (result.errors?.length > 0) {
                setError('otp', { type: 'custom', message: result.errors[0].message });
                toast({
                    description: result.errors[0].message,
                    variant: 'warning',
                });
                setFormCredentials(null);
                setIsLoading(false);
                return;
            }
            
            const method = result.data?.check2faMethod?.method;
            
            if (method === 'totp') {
                // User has TOTP enabled - go directly to TOTP input
                setStep('totp');
            } else {
                // Default to SMS flow - send OTP first
                await sendSmsOtp(formData);
            }
        } catch (e) {
            console.error(e);
            toast({
                description: 'An error occurred. Please try again.',
                variant: 'warning',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Send SMS OTP (existing flow)
    const sendSmsOtp = async (formData: any) => {
        setIsLoading(true);
        const otp: any = await sendOtp(formData.email, formData.password);
        if (otp.errors?.length > 0) {
            setError('otp', { type: 'custom', message: otp.errors[0].message });
            toast({
                description: otp.errors[0].message,
                variant: 'warning',
            });
            setFormCredentials(null);
            setIsLoading(false);
            return;
        }
        if (otp.data?.sendOtpCode?.success) {
            NOW = Date.now();
            const phone = otp.data?.sendOtpCode?.phoneNumber;
            const method = otp.data?.sendOtpCode?.otpMethod;
            setPhoneNo(phone);
            setOtpMethod(method);
            setStep('sms-otp');
        }
        setIsLoading(false);
    };

    // Go back to credentials
    const goBackToCredentials = () => {
        setStep('credentials');
        setOtpValue(null);
        setPhoneNo(null);
        setOtpMethod(null);
        clearErrors();
    };

    // Submit SMS OTP (existing flow)
    const onSmsOtpSubmit = async () => {
        setIsLoading(true);
        try {
            const { errors, data }: any = await userLogin(formCredentials?.email, formCredentials?.password, otpValue);
            if (errors?.length > 0) {
                setError('otp', { type: 'custom', message: errors[0].message });
            }
            if (data?.tokenAuth) {
                Cookies.set('token', data.tokenAuth?.token);
                Cookies.set('refreshToken', data.tokenAuth.refreshToken, { expires: 19 });
                navigate(AppRoutes.dashboard);
            }
        } catch (e: any) {
            console.error(e);
            const statusCode = e?.networkError?.statusCode;
            if (statusCode === 429) {
                setError('otp', { type: 'custom', message: t('rateLimitError', { defaultValue: 'Too many login attempts. Please wait a minute before trying again.' }) });
            } else {
                toast({
                    description: t('networkError', { defaultValue: 'A network error occurred. Please check your connection and try again.' }),
                    variant: 'warning',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Submit TOTP code (new flow)
    const onTotpSubmit = async () => {
        setIsLoading(true);
        try {
            const { errors, data }: any = await verifyTotpLogin(
                formCredentials?.email,
                formCredentials?.password,
                otpValue
            );
            if (errors?.length > 0) {
                setError('otp', { type: 'custom', message: errors[0].message });
            }
            if (data?.verifyTotpLogin?.token) {
                Cookies.set('token', data.verifyTotpLogin.token);
                Cookies.set('refreshToken', data.verifyTotpLogin.refreshToken, { expires: 19 });
                navigate(AppRoutes.dashboard);
            }
        } catch (e: any) {
            console.error(e);
            const statusCode = e?.networkError?.statusCode;
            if (statusCode === 429) {
                setError('otp', { type: 'custom', message: t('rateLimitError', { defaultValue: 'Too many login attempts. Please wait a minute before trying again.' }) });
            } else {
                toast({
                    description: t('networkError', { defaultValue: 'A network error occurred. Please check your connection and try again.' }),
                    variant: 'warning',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Switch from TOTP to SMS flow
    const switchToSms = async () => {
        setOtpValue(null);
        clearErrors();
        await sendSmsOtp(formCredentials);
    };

    const countdownRenderer = ({ seconds }: { seconds: number }) => {
        return (
            <div>
                {t('resendCodeIn')}
                {seconds < 10 ? `0${seconds}` : seconds}
            </div>
        );
    };

    // Credentials form
    if (step === 'credentials') {
        return (
            <form
                onSubmit={handleSubmit(onSubmit)}
                className={cn(styles.content, 'flex flex-col gap-8')}
                onChange={() => clearErrors()}
            >
                <Typography.H3 className={cn('text-zinc-700')}>{t('loginTitle')}</Typography.H3>
                <div className={cn('flex flex-col gap-6')}>
                    <div>
                        <Label className={cn('text-zinc-700')}>{t('email')}</Label>
                        <Input
                            placeholder={t('email')}
                            type="email"
                            {...register('email', {
                                required: true,
                                pattern: EMAIL_REGEX,
                            })}
                            error={!!errors.otp}
                        />
                    </div>
                    <div>
                        <Label className={cn('text-zinc-700')}>{t('password')}</Label>
                        <Input
                            placeholder={t('password')}
                            type="password"
                            {...register('password', { required: true })}
                            error={!!errors.otp}
                        />
                        {errors.otp && (
                            <div className={cn('text-orange-700 text-sm mt-2')}>{errors.otp.message as any}</div>
                        )}
                        <div className={cn('flex justify-end text-zinc-500')}>
                            <a href={`${base}${AppRoutes.forgotPassword}`}>{t('forgotPassword')}</a>
                        </div>
                    </div>
                </div>
                <div className={cn('flex justify-end')}>
                    <Button type="submit" disabled={!isValid || isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('loginButton')}
                    </Button>
                </div>
            </form>
        );
    }

    // SMS OTP form
    if (step === 'sms-otp') {
        // Show error if no OTP method was returned (shouldn't happen, but handle gracefully)
        if (!otpMethod) {
            return (
                <div className={cn(styles.content, 'flex flex-col gap-8')}>
                    <Typography.H3 className={cn('text-zinc-700')}>{t('otpTitle')}</Typography.H3>
                    <div className={cn('flex flex-col gap-4')}>
                        <p className="text-orange-600">
                            {t('noPhoneConfigured')}
                        </p>
                    </div>
                    <div className={cn('flex justify-start')}>
                        <Button variant="outline" onClick={goBackToCredentials}>
                            {t('back')}
                        </Button>
                    </div>
                </div>
            );
        }

        const isEmailOtp = otpMethod === 'email';

        return (
            <form className={cn(styles.content, 'flex flex-col gap-8')} onSubmit={e => e.preventDefault()}>
                <Typography.H3 className={cn('text-zinc-700')}>{t('otpTitle')}</Typography.H3>
                <div className={cn('flex flex-col gap-4')}>
                    <div className={cn('flex items-center gap-2 text-zinc-500')}>
                        <Smartphone className="w-4 h-4" />
                        <span>{isEmailOtp ? t('emailVerification') : 'SMS verification'}</span>
                    </div>
                    <p>
                        {isEmailOtp 
                            ? t('otpEmailSubtitle')
                            : `${t('otpSubtitle')} ${phoneNo}.`
                        }
                    </p>
                    <InputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS} onChange={val => setOtpValue(val)}>
                        <InputOTPGroup>
                            <InputOTPSlot index={0} error={!!errors.otp} />
                            <InputOTPSlot index={1} error={!!errors.otp} />
                            <InputOTPSlot index={2} error={!!errors.otp} />
                            <InputOTPSlot index={3} error={!!errors.otp} />
                            <InputOTPSlot index={4} error={!!errors.otp} />
                            <InputOTPSlot index={5} error={!!errors.otp} />
                        </InputOTPGroup>
                    </InputOTP>
                    {errors.otp && <div className={cn('text-orange-700 text-sm')}>{errors.otp.message as any}</div>}
                    {showCounter ? (
                        <Countdown
                            date={NOW + 59000}
                            renderer={countdownRenderer}
                            onComplete={() => setShowCounter(false)}
                        />
                    ) : (
                        <div
                            className={cn('text-rose-700 underline hover:cursor-pointer hover:text-rose-900')}
                            onClick={() => resendCode()}
                        >
                            {t('sendNewCode')}
                        </div>
                    )}
                </div>
                <div className={cn('flex justify-between')}>
                    <Button variant="ghost" onClick={goBackToCredentials}>
                        {t('back')}
                    </Button>
                    <Button onClick={onSmsOtpSubmit} disabled={!otpValue || isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('otpButton')}
                    </Button>
                </div>
            </form>
        );
    }

    // TOTP form (authenticator app)
    if (step === 'totp') {
        return (
            <form className={cn(styles.content, 'flex flex-col gap-8')} onSubmit={e => e.preventDefault()}>
                <Typography.H3 className={cn('text-zinc-700')}>Authenticator Code</Typography.H3>
                <div className={cn('flex flex-col gap-4')}>
                    <div className={cn('flex items-center gap-2 text-zinc-500')}>
                        <KeyRound className="w-4 h-4" />
                        <span>Authenticator app</span>
                    </div>
                    <p>Enter the 6-digit code from your authenticator app or a backup code.</p>
                    <InputOTP maxLength={8} onChange={val => setOtpValue(val)}>
                        <InputOTPGroup>
                            <InputOTPSlot index={0} error={!!errors.otp} />
                            <InputOTPSlot index={1} error={!!errors.otp} />
                            <InputOTPSlot index={2} error={!!errors.otp} />
                            <InputOTPSlot index={3} error={!!errors.otp} />
                            <InputOTPSlot index={4} error={!!errors.otp} />
                            <InputOTPSlot index={5} error={!!errors.otp} />
                            <InputOTPSlot index={6} error={!!errors.otp} />
                            <InputOTPSlot index={7} error={!!errors.otp} />
                        </InputOTPGroup>
                    </InputOTP>
                    {errors.otp && <div className={cn('text-orange-700 text-sm')}>{errors.otp.message as any}</div>}
                    <div
                        className={cn('text-zinc-500 underline hover:cursor-pointer hover:text-zinc-700')}
                        onClick={switchToSms}
                    >
                        Use SMS verification instead
                    </div>
                </div>
                <div className={cn('flex justify-between')}>
                    <Button variant="ghost" onClick={goBackToCredentials}>
                        {t('back')}
                    </Button>
                    <Button onClick={onTotpSubmit} disabled={!otpValue || isLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Verify
                    </Button>
                </div>
            </form>
        );
    }

    return null;
};
